<script setup lang="ts">
import { LoanType } from '@/helpers/const';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useLoanAppProcess } from '@/helpers/useLoanAppProcess';
import {
  type AssetId,
  CustomerApplicationProcessStep,
  type PropertyId,
  StepStatus,
  type ApplicantId,
  type PropertyOwnership,
} from '@/types';
import { computed, onBeforeMount, ref } from 'vue';
import OwnershipForm from './modules/PropertyOwnership/OwnershipForm.vue';

const { send, stepper, loanApp, loanProperties, isSaving, submitStep, saveAndGoNext, goBack } = useLoanAppProcess();

const app: Record<ApplicantId, number> = {};

if (loanApp.value.applicants.length === 1) {
  app[loanApp.value.applicants[0].id] = 100;
} else if (loanApp.value.applicants.length === 2) {
  const ids = loanApp.value.applicants.map((a) => a.id);
  app[ids[0]] = 50;
  app[ids[1]] = 50;
} else {
  loanApp.value.applicants.forEach((item) => {
    app[item.id] = -1;
  });
}

const properties = [
  ...(loanApp.value.loanType === LoanType.BRIDGING ? loanApp.value.incomingProperties : []),
  ...loanApp.value.outgoingProperties,
];

const po: PropertyOwnership[] = properties.map((item) => ({
  id: item.id,
  type: 'contractForSale' in item ? 'incoming' : 'outgoing',
  share: { ...app },
}));

const origOwnership = loanApp.value.propertiesOwnership;
const origApplicants = origOwnership.length ? Object.keys(origOwnership[0].share) : [];
const newApplicants = Object.keys(app);

if (origApplicants.length === newApplicants.length && newApplicants.every((a) => origApplicants.includes(a))) {
  for (let i = 0; i < po.length; i++) {
    const property = origOwnership.find((o) => o.id === po[i].id);
    if (property) {
      po[i].share = property.share;
    }
  }
}

const propertyOwnership = ref<PropertyOwnership[]>(po);

const showValidation = ref(false);

const propertyValidation = ref<
  Record<PropertyId | AssetId, { isValid: boolean; total: number; shouldShowValidation: boolean }>
>({});

const fullOwnership = computed(() =>
  propertyOwnership.value.every((p) => {
    const total = Object.values(p.share).reduce((sum, share) => sum + (share === -1 ? 0 : Number(share) || 0), 0);
    return total === 100;
  }),
);

const checkOwnership = (index: PropertyId | AssetId) => {
  if (!showValidation.value) return true;

  const property = propertyOwnership.value.find((p) => p.id === index);
  if (!property) return true;

  const total = Object.values(property.share).reduce((sum, share) => sum + (share === -1 ? 0 : Number(share) || 0), 0);
  return total === 100;
};

const handlePropertyValidation = (
  propertyId: PropertyId | AssetId,
  validation: { isValid: boolean; total: number; shouldShowValidation: boolean },
) => {
  propertyValidation.value[propertyId] = validation;
};

const singleApplicant = computed(() => loanApp.value.applicants.length === 1);

const validateOwnership = () =>
  propertyOwnership.value.every((p) => {
    const total = Object.values(p.share).reduce((sum, share) => sum + (share === -1 ? 0 : Number(share) || 0), 0);
    return total === 100;
  });

const saveAndContinue = () =>
  submitStep(CustomerApplicationProcessStep.PropertyOwnership, async () => {
    showValidation.value = true;

    const cleanedOwnership = propertyOwnership.value.map((property) => ({
      ...property,
      share: Object.fromEntries(
        Object.entries(property.share).map(([applicantId, share]) => [
          applicantId,
          share === -1 ? 0 : Number(share) || 0,
        ]),
      ),
    }));

    propertyOwnership.value = cleanedOwnership;

    if (validateOwnership()) {
      await saveAndGoNext({
        propertiesOwnership: propertyOwnership.value,
      });
    }
  });

const isReady = ref(false);
const calc = useCalcApi();
onBeforeMount(async () => {
  isReady.value = false;
  try {
    if (
      loanApp.value.loanDetails &&
      stepper.value.find((s) => s.id === CustomerApplicationProcessStep.PropertyOwnership)?.status === StepStatus.Ready
    ) {
      const endDebt = (await calc.getEndDebt(loanApp.value)) ?? 0;
      send({
        type: 'UPDATE',
        loanApp: { ...loanApp.value, endDebt },
      });
    }
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the
        <BxHighlight>ownership percentage</BxHighlight>
        of the properties you are buying and selling?
      </h2>
    </div>

    <template v-for="(property, propertyIndex) in propertyOwnership" :key="property.id">
      <BxExpander class="mt-6" separator overflow-visible>
        <template #header>
          <div class="px-4 py-5 sm:px-6">
            <template v-if="loanProperties[property.id]">
              <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                  <span class="mr-3 hidden md:block">
                    <BxIcon :name="property.type === 'incoming' ? 'incomingProperty' : 'outgoingProperty'" />
                  </span>
                  <span>{{ loanProperties[property.id] }}</span>
                </h3>
              </div>
              <div class="pb-3">
                <OwnershipForm
                  v-model="property.share"
                  :asset-number="propertyIndex"
                  :error="
                    !checkOwnership(property.id) ||
                    (propertyValidation[property.id]?.shouldShowValidation && !propertyValidation[property.id]?.isValid)
                  "
                  class="mt-2"
                  @validation="handlePropertyValidation(property.id, $event)"
                />
              </div>
            </template>
            <div
              v-if="propertyValidation[property.id]?.shouldShowValidation && !propertyValidation[property.id]?.isValid"
            >
              <p class="text-rose-500">Ownership must total to 100% for this property.</p>
            </div>
          </div>
        </template>
      </BxExpander>
    </template>
    <div class="mt-6 flex gap-4">
      <BxButton v-if="!fullOwnership && singleApplicant" variant="primary" @click="goBack()">
        Add new applicant
      </BxButton>
      <BxButton
        v-else
        id="property-ownership-continue"
        :loading="!isReady || isSaving"
        variant="primary"
        @click="saveAndContinue()"
      >
        Save and continue
      </BxButton>
      <BxButton variant="link" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>
